/**
 * Fill-rate trends across the runs this browser still holds.
 *
 * Pure: the caller reads the analyses table, this decides what the rows mean
 * together. Nothing is inferred and nothing is estimated — every point is a
 * fill rate a run measured and recorded.
 *
 * Two constraints shape it:
 *
 * - **Same source only.** Fill rates from different sources are different
 *   fields with the same names; charting them on one line would invent a
 *   comparison nobody made. A run whose cache row predates profiles being
 *   required carries no profile id and takes part in no trend.
 * - **What is still cached, not what ever ran.** The analyses table is bounded
 *   (`ANALYSIS_CACHE_MAX_ENTRIES`), so this is a window. The count travels with
 *   the result so the UI can say so rather than implying a complete history.
 *
 * `ranAt` follows the same rule as the run history (`features/runs/history.ts`):
 * the review's `generatedAt` is when the run happened; `createdAt` is only when
 * the row reached the cache.
 */

/** The slice of a cached analysis a trend needs. */
export type TrendAnalysis = {
  analysisKey: string;
  createdAt: string;
  result: {
    fieldStats: Array<{ field: string; latestPresentRate: number }>;
  };
  review?: { profileId: string; generatedAt: string } | null;
};

export type TrendPoint = {
  analysisKey: string;
  /** When the run happened; the cache timestamp when the run did not say. */
  ranAt: string;
  /** Share of records where the field carried a value, in that run's latest file. */
  rate: number;
  /** True when this point is the run currently on screen. */
  current: boolean;
};

export type FieldTrend = {
  field: string;
  /** Oldest first, so the line reads left to right as time. */
  points: TrendPoint[];
  first: number;
  last: number;
  min: number;
  max: number;
  /** max - min: how far the field moved across the window, the ranking key. */
  range: number;
};

export type RunTrends = {
  /** Runs of this source in the cache, the current one included. */
  runCount: number;
  /** Fields with a rate in at least two of those runs, moved or not. */
  comparedFieldCount: number;
  /** Of those, how many moved at all. Zero is a finding in itself. */
  movedFieldCount: number;
  /** The widest movers, largest range first, capped. */
  trends: FieldTrend[];
  truncated: boolean;
};

/**
 * How many fields the Overview charts. A source here carries tens of fields and
 * a wall of flat sparklines hides the two that moved; the rest stay one click
 * away on Field Changes.
 */
export const TREND_FIELD_CAP = 8;

export function buildRunTrends(input: {
  analyses: TrendAnalysis[];
  /** The source whose runs to chart; null when the current run has no profile. */
  profileId: string | null;
  /**
   * The run on screen, taken from the app's own state rather than the cache.
   *
   * The cache write is asynchronous and the Overview renders as soon as the
   * analysis finishes, so a panel built from the table alone can miss the very
   * run the reader is looking at — and would then claim this is the only run of
   * the source. A cache row with the same key is superseded by this one.
   */
  current: TrendAnalysis | null;
}): RunTrends {
  const empty: RunTrends = { runCount: 0, comparedFieldCount: 0, movedFieldCount: 0, trends: [], truncated: false };
  if (input.profileId === null) return empty;

  const currentAnalysisKey = input.current?.analysisKey ?? null;
  const runs = [
    ...input.analyses.filter((entry) => entry.analysisKey !== currentAnalysisKey),
    ...(input.current === null ? [] : [input.current])
  ]
    .filter((entry) => entry.review?.profileId === input.profileId)
    .map((entry) => ({
      analysisKey: entry.analysisKey,
      ranAt: entry.review?.generatedAt ?? entry.createdAt,
      fieldStats: entry.result.fieldStats
    }))
    // Oldest first. Ties break on the key so the order is stable rather than
    // dependent on the table's iteration order.
    .sort((left, right) => left.ranAt.localeCompare(right.ranAt) || left.analysisKey.localeCompare(right.analysisKey));

  if (runs.length < 2) return { ...empty, runCount: runs.length };

  const byField = new Map<string, TrendPoint[]>();
  for (const run of runs) {
    for (const stat of run.fieldStats) {
      if (!Number.isFinite(stat.latestPresentRate)) continue;
      const points = byField.get(stat.field) ?? [];
      points.push({
        analysisKey: run.analysisKey,
        ranAt: run.ranAt,
        rate: stat.latestPresentRate,
        current: run.analysisKey === currentAnalysisKey
      });
      byField.set(stat.field, points);
    }
  }

  const trends: FieldTrend[] = [];
  let comparedFieldCount = 0;
  for (const [field, points] of byField) {
    // One point is a value, not a trend: a field that appeared for the first
    // time in this run has nothing to compare against yet.
    if (points.length < 2) continue;
    comparedFieldCount += 1;
    const rates = points.map((point) => point.rate);
    const min = Math.min(...rates);
    const max = Math.max(...rates);
    // A flat line is not a trend. Charting every steady field would bury the two
    // that moved under a wall of unchanged ones — and a run where NOTHING moved
    // is worth saying in a sentence rather than drawing 45 times.
    if (max === min) continue;
    trends.push({
      field,
      points,
      first: rates[0]!,
      last: rates[rates.length - 1]!,
      min,
      max,
      range: max - min
    });
  }

  trends.sort((left, right) => right.range - left.range || left.field.localeCompare(right.field));

  return {
    runCount: runs.length,
    comparedFieldCount,
    movedFieldCount: trends.length,
    trends: trends.slice(0, TREND_FIELD_CAP),
    truncated: trends.length > TREND_FIELD_CAP
  };
}
